import React, {useEffect, useState} from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  Theme,
  makeStyles,
  createStyles,
} from "@material-ui/core";

import ClickAgreements from "modules/Checkout/components/ClickAgreements";
import CheckoutUI from "ares-core/UI/Checkout/CheckoutUI";
import {
  ICheckoutData,
  IContactInfo,
  IAddressMultiline,
} from "ares-core/Models";
import { Shopping, IShoppingStore } from "ares-core/Shopping";
import { Locale } from "ares-core/UI";
import { MathEx } from "ares-core";
import { HowDidYouFindUsQuestion } from "ares-core/UI/Checkout/HowDidYouFindUsQuestion";
import ContactInfo, { allValid, generateValidityObj } from "ares-core/UI/ContactInfo";
import AddressMultiline from "ares-core/UI/AddressMultiLine";
import * as CheckoutUtils from "ares-core/Utils/CheckoutUtils";
import CartDisplay from "modules/Cart/components/CartDisplay";
import * as ShoppingUtils from "ares-core/Shopping/HelperFunctions";
import SubmitButtonLoading from "ares-core/UI/Components/SubmitButtonLoading";
import { GatewayID } from "ares-core/UI/Checkout/Gateway";
import StripePaymentElement from "./StripePaymentElement";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      borderRadius: theme.shape.borderRadius,
      borderColor: theme.palette.grey[400],
      borderWidth: "1px",
      padding: "0.5em",
      borderStyle: "solid",
    },
    hasFocus: {
      borderColor: theme.palette.primary.main,
      borderWidth: "2px",
    },
    hasError: {
      borderColor: theme.palette.error.main,
    },
    submit: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
    },
  }),
);

const checkoutDataEmpty = {
  multilineAddress: {
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
  },
  contactInfo: {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  },
  token: "",
};

export interface ICheckoutProps extends IShoppingStore {
  gatewayProcessor: any;
  companyEntry: boolean;
  triggerReady?: boolean;
  isTest?: boolean;
}

export const Checkout = (props: ICheckoutProps) => {
  const history = useHistory();
  const classes = useStyles();
  const [hasError, setHasError] = useState("");
  const [clickAgreementsOpen, setClickAgreementsOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [checkoutData, setCheckoutData] = useState<ICheckoutData>(checkoutDataEmpty);
  const [proceedWithCheckout, setProceedWithCheckout] = useState<boolean>(false);

  const [contactInfoIsValid, setContactInfoIsValid] = useState<boolean>(false);
  const [addressIsValid, setAddressIsValid] = useState<boolean>(false);
  const [paymentDataIsValid, setPaymentDataIsValid] = useState<boolean>(false);
  const [howHeardIsValid, setHowHeardIsValid] = useState<boolean>(false);

  const displyCheckoutUI = !props.isTest && 
  (props.gatewayProcessor?.gatewayID() === GatewayID.MPPG || 
   props.gatewayProcessor?.gatewayID() === GatewayID.Square ||
   props.gatewayProcessor?.gatewayID() === GatewayID.Stripe ||
   props.gatewayProcessor?.gatewayID() === GatewayID.StripeConnect);

  const displayStripeIntent = !props.isTest && props.gatewayProcessor?.gatewayID() === GatewayID.StripeIntent;

  useEffect(()=> {
    let newContactData = {...checkoutData}
    let contact:IContactInfo | null = null;
    if(props.billingInfo.firstName && props.billingInfo.lastName) {
       contact = {
        firstName: props.billingInfo.firstName,
        lastName: props.billingInfo.lastName,
        email: props.billingInfo.email,
        phone: props.billingInfo.phone
      }
    } else {
      const contactStr: any | null = localStorage.getItem('contact')
      if (contactStr) {
        contact = JSON.parse(contactStr || '{}');
        Shopping.Actions.SetBillingInfo(contact);
      }
    }
    if(contact) {
      newContactData = {...newContactData,contactInfo: contact}
      const isValid = allValid(generateValidityObj(contact)({
        email: false,
        lastName: false,
        phone: false,
        firstName: false,
      } ),false);
      setContactInfoIsValid(isValid);
    }

    const addressStr: any | null = localStorage.getItem('billingAddress')
    if (addressStr) {
      const address: IAddressMultiline = JSON.parse(addressStr || '{}');
      setAddressIsValid(true);
      Shopping.Actions.SetBillingAddress(address);
      newContactData = {...newContactData, multilineAddress: address}
    }
    setCheckoutData(newContactData);
  },[])

  useEffect(() => {
    if(isFullyPaidByGiftCard && proceedWithCheckout) {
      // Simulate filling credit card, we're using credit card
      handlePaymentWithGiftCard();
    }
  }, [proceedWithCheckout]);


  useEffect(()=> {
    validateHowHeardAnswer();
  }, [props.howHeardAnswers, props.howHeardAnswerID]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true)
    if (clickAgreementsOpen || proceedWithCheckout) {
      return;
    } // Prevent Spamming

    // Clear any current Error
    setHasError("");
    if (!howHeardIsValid) {
      setLoading(false)
      setHasError(Locale.invalidHowHeardSelection);
    } else if (!contactInfoIsValid) {
      setLoading(false)
      setHasError(Locale.invalidContactInfo);
    } else if (!displayStripeIntent && !addressIsValid && !isFullyPaidByGiftCard) {   // Address fields are hidden if displayStripeIntent === true
      setLoading(false)
      setHasError(Locale.invalidAddressInfo);
    } else if (!paymentDataIsValid && !isFullyPaidByGiftCard) {
      setLoading(false)
      setHasError(Locale.invalidPaymentInfo);
    } else if (!clickAgreementsOpen && !proceedWithCheckout) {
      setClickAgreementsOpen(true);
    }
  };

  // User has agreed to the Company Agreements
  const handleClickAgreementContinue = () => {
    setClickAgreementsOpen(false);
    if (!proceedWithCheckout) {
      // prevent spamming
      setProceedWithCheckout(true); // Triggers PaymentProcessor to acquire token
    }
  };

  // User has declined/canceled
  const handleClickAgreementCancel = () => {
    setLoading(false)
    setClickAgreementsOpen(false);
  };

  // Initially set to zero or company Default
  const handleHowDidYouFindUsChange = (value: number) => {
    Shopping.Actions.SetShoppingHowHeard(value);
    if(!value || value === 0) {
      setHowHeardIsValid(false);
    } else {
      setHowHeardIsValid(true);
      setHasError("");
    }
  };

  const handleContactInfoChange = (
    contactInfo: IContactInfo,
    isValid: boolean,
  ) => {
    setCheckoutData({ ...checkoutData, contactInfo });
    setContactInfoIsValid(isValid);
    if (isValid) {
      Shopping.Actions.SetBillingInfo(contactInfo);
      if (hasError.length > 0) {
        setHasError("");
      }
    }
  };

  const handleAddressChange = (
    multiLineAddress: IAddressMultiline,
    isValid: boolean,
  ) => {
    setCheckoutData({ ...checkoutData, multilineAddress: multiLineAddress });
    setAddressIsValid(isValid);
    if (isValid) {
      Shopping.Actions.SetBillingAddress(multiLineAddress);
      localStorage["billingAddress"] = JSON.stringify(multiLineAddress);
      if (hasError.length > 0) {
        setHasError("");
      }
    }
  };

  // User has entered data into the payment component
  const handlePaymentProcessorChange = (isValid: boolean, data?: any) => {
    setPaymentDataIsValid(isValid);
    if (isValid && hasError.length > 0) {
      setHasError("");
    }
  };
  const handleRedirectOnPaymentProcessed = () => {
    const slug = Shopping.Store().companyId;
    const receipt = Shopping.Store().checkoutTransaction;
    if (receipt) {
      ShoppingUtils.ClearCartId(slug);
      // Used browser api, because react was forcing navigation to homeredirect
      window.history.replaceState(
        null,
        "Redirect",
        `/homeredirect/${slug}`,
      );
      history.push(`/Receipt/${slug}/${receipt}`);
    } else {
      const cart = Shopping.Store().cart;
      if (cart.errors?.length > 0) {
        history.push(`/cart/${slug}`);
      }
    }
  }
  const regularCheckout = () => {
    Shopping.Api.checkout().then(() => {
      handleRedirectOnPaymentProcessed();
    }).catch((error: any) => {
      setProceedWithCheckout(false);  // Allows a new checkout attempt.
    }).finally(() => {
      setLoading(false);
    });
  }
  const giftCardCheckout = () => {
    Shopping.Api.checkout(true).then(() => {
      handleRedirectOnPaymentProcessed();
    }).catch((error: any) => {
      setProceedWithCheckout(false);  // Allows a new checkout attempt.
    }).finally(() => {
      setLoading(false);
    });
  }
  const handlePaymentWithGiftCard = () => {
    setProceedWithCheckout(false); // Allows another checkout
    giftCardCheckout();
    // Shopping.Actions.SetProcessorData(data); // Checkout Thunk Reads from Store

  }
  // User has entered the Processor necessary
  const handlePaymentReady = (valid: boolean, data: string, errorMessage?: string) => {
    if (valid) {
      setProceedWithCheckout(false); // Allows another checkout
      Shopping.Actions.SetProcessorData(data); // Checkout Thunk Reads from Store
      regularCheckout();
    } else {
      setLoading(false);
      setProceedWithCheckout(false);  // Allows a new checkout attempt.
      if(errorMessage) {
        setHasError(errorMessage)
      } else {
        // If the payment control wasn't valid, it should have provided an error message.
        setHasError("");
      }
    }
  };

  const validateHowHeardAnswer = () => {
    const shopping = Shopping.Store();
    if(!shopping.howHeardAnswers || shopping.howHeardAnswers.length === 0) {
      setHowHeardIsValid(true);
    } else {
      const valid = Shopping.Store().howHeardAnswerID ? true : false;
      setHowHeardIsValid(valid);
    }
  };

  const taxAndFees = CheckoutUtils.calculateTaxesAndFees(props.cart);
  const totalWithFees = props.cart.subTotal + taxAndFees;
  const totalCheckout = totalWithFees - Shopping.GiftCardTotal();
  const depositBalance = props.cart.depositBalance;
  const isFullyPaidByGiftCard = totalWithFees > 0 && Shopping.GiftCardTotal() > 0 && totalCheckout === 0;

  return (
    <Box>
      <Typography variant="h5">Checkout</Typography>
      <FormControl variant="outlined" fullWidth >
        <form onSubmit={handleSubmit} name='checkoutForm'>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <HowDidYouFindUsQuestion
                answers={props.howHeardAnswers}
                answer={props.howHeardAnswerID!}
                onChange={handleHowDidYouFindUsChange}
              />
            </Grid>

            {
              (displyCheckoutUI && !isFullyPaidByGiftCard) &&
              <Grid item xs={12}>
                  <CheckoutUI
                    checkoutData={checkoutData}
                    onChange={handlePaymentProcessorChange}
                    gatewayProcessor={props.gatewayProcessor}
                    paymentIntentCode=""
                    proceedWithCheckout={proceedWithCheckout}
                    onPaymentReady={handlePaymentReady}
                    processorSpecificJson={""}
                  />

              </Grid>
            }

            <Grid item xs={12}>
              <ContactInfo
                contactInfo={checkoutData.contactInfo}
                onChange={handleContactInfoChange}
                companyEntry={props.companyEntry}
                country={checkoutData.multilineAddress.country?.toLowerCase()}
              />

              {
                (!displayStripeIntent && !isFullyPaidByGiftCard) &&
                <>
                    <AddressMultiline
                      multiLineAddress={checkoutData.multilineAddress}
                      onChange={handleAddressChange}
                      onlyRequireZip={props.companyEntry}
                    />
                </>
              }

              <Typography style={{ marginTop: "10px" }}>
                <b>{Locale.cart}</b>
              </Typography>

              {
                !props.cart.isEmpty && 
                  <CartDisplay condensed={true} cartDto={props.cart} /> 
              }

              {
                (displayStripeIntent && !isFullyPaidByGiftCard) &&
                <StripePaymentElement
                  checkoutData={checkoutData}
                  onChange={handlePaymentProcessorChange}
                  gatewayProcessor={props.gatewayProcessor}
                  proceedWithCheckout={proceedWithCheckout}
                  onPaymentReady={handlePaymentReady}
                />
              }

              {
                hasError.length > 0 ? 
                  <Typography style={{ color: '#F44336', fontSize: '15px', fontWeight: 600}}>
                    {hasError}
                  </Typography> :
                  null
              }

              <Box className={classes.submit}>
                <SubmitButtonLoading 
                  showLoading={isLoading} 
                  type="submit" 
                  variant={"outlined"} 
                  color={"primary"}>                                                                                                                            
                  {Locale.continue} {Locale.checkingOut} {MathEx.formatCurrency(totalCheckout)}
                </SubmitButtonLoading>
              </Box>
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                  
            </Grid>
          </Grid>


        </form>
      </FormControl>

      <ClickAgreements
        agreements={props.cart.clickAgreements}
        handleCancel={handleClickAgreementCancel}
        handleClose={handleClickAgreementContinue}
        open={clickAgreementsOpen}
      />
    </Box>
  );
};
